import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateToISO } from '../../utils/formatters';
import { ArrowLeft, Download, Printer } from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const APAgingReportPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [asOfDate, setAsOfDate] = useState(formatDateToISO(new Date()));

  useEffect(() => {
    fetchReport();
  }, [asOfDate]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/bills/aging-report`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { as_of_date: asOfDate }
      });
      setReportData(response.data?.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch AP aging report');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const getTotalByBucket = (bucket) => {
    if (!reportData?.vendors) return 0;
    return reportData.vendors.reduce((sum, vendor) => {
      return sum + (parseFloat(vendor[bucket]) || 0);
    }, 0);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 print:hidden">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/accounting/bills')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">AP Aging Report</h1>
            <p className="text-sm text-gray-500">Accounts Payable aging by vendor</p>
          </div>
        </div>
        <div className="flex gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">As of Date</label>
            <input
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <button
            onClick={handlePrint}
            className="self-end bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Printer className="h-4 w-4" />
            Print
          </button>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block mb-6">
        <h1 className="text-2xl font-bold text-gray-900 text-center">
          Accounts Payable Aging Report
        </h1>
        <p className="text-sm text-gray-600 text-center">As of {formatDate(asOfDate)}</p>
        <p className="text-sm text-gray-600 text-center">Eldaved Business Management System</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6 print:hidden">
          {error}
        </div>
      )}

      {reportData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6 print:hidden">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Current</div>
              <div className="text-lg font-bold text-green-600">
                {formatCurrency(getTotalByBucket('current'))}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">31-60 Days</div>
              <div className="text-lg font-bold text-yellow-600">
                {formatCurrency(getTotalByBucket('days_31_60'))}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">61-90 Days</div>
              <div className="text-lg font-bold text-orange-600">
                {formatCurrency(getTotalByBucket('days_61_90'))}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Over 90 Days</div>
              <div className="text-lg font-bold text-red-600">
                {formatCurrency(getTotalByBucket('over_90_days'))}
              </div>
            </div>
          </div>

          {/* Report Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Vendor
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Current
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      31-60 Days
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      61-90 Days
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Over 90
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {reportData.vendors && reportData.vendors.length > 0 ? (
                    <>
                      {reportData.vendors.map((vendor) => (
                        <tr key={vendor.vendor_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {vendor.vendor_name}
                          </td>
                          <td className="px-6 py-4 text-sm text-right text-green-600">
                            {formatCurrency(vendor.current)}
                          </td>
                          <td className="px-6 py-4 text-sm text-right text-yellow-600">
                            {formatCurrency(vendor.days_31_60)}
                          </td>
                          <td className="px-6 py-4 text-sm text-right text-orange-600">
                            {formatCurrency(vendor.days_61_90)}
                          </td>
                          <td className="px-6 py-4 text-sm text-right text-red-600">
                            {formatCurrency(vendor.over_90_days)}
                          </td>
                          <td className="px-6 py-4 text-sm text-right font-bold text-gray-900">
                            {formatCurrency(vendor.total)}
                          </td>
                        </tr>
                      ))}
                      {/* Totals Row */}
                      <tr className="bg-gray-50 font-bold">
                        <td className="px-6 py-4 text-sm text-gray-900">TOTAL</td>
                        <td className="px-6 py-4 text-sm text-right text-green-700">
                          {formatCurrency(getTotalByBucket('current'))}
                        </td>
                        <td className="px-6 py-4 text-sm text-right text-yellow-700">
                          {formatCurrency(getTotalByBucket('days_31_60'))}
                        </td>
                        <td className="px-6 py-4 text-sm text-right text-orange-700">
                          {formatCurrency(getTotalByBucket('days_61_90'))}
                        </td>
                        <td className="px-6 py-4 text-sm text-right text-red-700">
                          {formatCurrency(getTotalByBucket('over_90_days'))}
                        </td>
                        <td className="px-6 py-4 text-sm text-right text-gray-900">
                          {formatCurrency(reportData.totals?.total || getTotalByBucket('total'))}
                        </td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                        No outstanding bills found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Report Footer */}
          <div className="mt-6 text-sm text-gray-500 print:block">
            <p>Generated on {new Date().toLocaleString('en-NG')}</p>
            <p>Report run by: {user?.email}</p>
          </div>
        </>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .p-4, .p-4 * {
            visibility: visible;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:block {
            display: block !important;
          }
        }
      `}</style>
    </div>
  );
};

export default APAgingReportPage;
